import type { Tracker } from '@archipro-website/tracker';

/**
 * Params whose comma is part of one value rather than a list separator: a
 * range is a single `min,max` pair, and a search term is free text.
 */
const isScalarParam = (key: string): boolean =>
    key === 'search' || key.endsWith('Range');

const splitParamValue = (key: string, value: string): string[] =>
    isScalarParam(key) ? [value] : value.split(',');

/** The query of a listing URL, without any `#anchor` after it. */
const toSearchParams = (url: string): URLSearchParams =>
    new URLSearchParams(url.split('#')[0]?.split('?')[1] ?? '');

/**
 * The facets a move from one listing URL to another added, one `key=value`
 * entry each.
 *
 * Multi-select facets share one comma-joined param (`colors=blue,red`), so the
 * values are compared one by one: ticking a second colour adds that colour,
 * not the whole list. Diffing URLs rather than filter states keeps this
 * independent of the state shape. Removals are not reported.
 */
export const getAddedFacets = (
    originalURL: string,
    newURL: string,
): string[] => {
    const before = toSearchParams(originalURL);
    const added: string[] = [];

    toSearchParams(newURL).forEach((value, key) => {
        const previous = new Set(
            before.getAll(key).flatMap((item) => splitParamValue(key, item)),
        );
        for (const item of splitParamValue(key, value)) {
            if (item && !previous.has(item)) added.push(`${key}=${item}`);
        }
    });

    return added;
};

/**
 * The params a move from one listing URL to another set or changed, whole
 * `key=value` entries — the legacy (pre-revamp) report, which treats a
 * multi-select param as one value.
 */
const getChangedParams = (originalURL: string, newURL: string): string[] => {
    const before = toSearchParams(originalURL);
    const changed: string[] = [];
    toSearchParams(newURL).forEach((value, key) => {
        if (!before.getAll(key).includes(value))
            changed.push(`${key}=${value}`);
    });
    return changed;
};

/** Where a filter was applied: a directory listing, or a professional's tab. */
export type FilterAppliedSource = 'directoryBrowse' | 'professionalTab';

const logFilterApplied = (
    tracker: Tracker,
    added: string[],
    source: FilterAppliedSource,
): void => {
    void tracker.log('FilterApplied', {
        url: new URL(window.location.href),
        targetTracker: ['archiproTrackerV2', 'ga4Tracker'],
        data: {
            Source: source,
            ExtraData: JSON.stringify({ added }),
        },
    });
};

/**
 * Logs the facets a filter or sort change added as `FilterApplied`.
 *
 * With `perFacet` (the revamp) it is one event per facet whatever the control:
 * the desktop sidebar applies each facet as it is ticked, while the mobile
 * refine sheet stages several and commits them together, and both have to
 * count the same. Without it, one event per change carries every changed
 * param, as the legacy pages have always reported. Only additions are
 * reported: `FilterApplied` has always meant a filter was applied, and
 * emitting on clear too would inflate it.
 */
export const reportFilterApplied = (
    tracker: Tracker,
    originalURL: string,
    newURL: string,
    perFacet: boolean,
    source: FilterAppliedSource = 'directoryBrowse',
): void => {
    if (!perFacet) {
        const changed = getChangedParams(originalURL, newURL);
        if (changed.length > 0) logFilterApplied(tracker, changed, source);
        return;
    }

    for (const facet of getAddedFacets(originalURL, newURL)) {
        logFilterApplied(tracker, [facet], source);
    }
};

/**
 * Logs a category committed from the refine sheet as `FilterApplied`. The
 * category is the URL's path rather than a query param, so the URL diff the
 * other filters go through never sees it.
 */
export const reportCategoryApplied = (
    tracker: Tracker,
    categoryUrl: string,
    source: FilterAppliedSource = 'directoryBrowse',
): void => {
    logFilterApplied(tracker, [`category=${categoryUrl}`], source);
};
